import { observer } from "mobx-react";
import type React from "react";
import { Trans, useTranslation } from "react-i18next";

import { AdminModals } from "constants/modals-constants";

import { useModal } from "hooks/useModal";

import { ModalRenderType } from "components/Modal/Modal.model";
import { Note } from "components/Note/Note";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { useAdmin } from "hooks/useAdmin";

import { Modal } from "components/Modal";

export interface ConfirmSignpostSchoolDeletionModalParams {
  schoolId: string;
  schoolName: string;
}

interface ConfirmSignpostSchoolDeletionModalProps {
  onSchoolDeleted: () => void;
}

export const ConfirmSignpostSchoolDeletionModal: React.FC<ConfirmSignpostSchoolDeletionModalProps> =
  observer(({ onSchoolDeleted }) => {
    const modalId = AdminModals.ConfirmSignpostSchoolDeletion;

    const { t } = useTranslation("modal");
    const { deleteSchool, loading: isDeleting } = useAdmin();
    const { closeModal, isModalOpen, modalParams } = useModal();

    const isModalCurrentlyOpen = isModalOpen(modalId);

    const { schoolId, schoolName } = (modalParams ||
      {}) as unknown as ConfirmSignpostSchoolDeletionModalParams;

    // Delete group and redirect back to group list page
    const onConfirmDeleteClick = () => {
      deleteSchool(schoolId).then(onSchoolDeleted).finally(closeModal);
    };

    return (
      <Modal
        dataCy={modalId}
        isOpen={isModalCurrentlyOpen}
        renderToElementType={ModalRenderType.REACT_PORTAL}
        onClose={closeModal}
        variant="rounded-border"
        width="37rem"
      >
        <CssFlex flexDirection="column" gap={1} padding="2rem">
          <Heading2>{t("modal:confirmSignpostSchoolDeletionModal.title")}</Heading2>
          <Trans
            components={{
              schoolNameTag: <b data-cy="target-school-name" />,
              p: <ParagraphLarge />,
            }}
            i18nKey="modal:confirmSignpostSchoolDeletionModal.description"
            t={t}
            values={{
              schoolName,
            }}
          />
          <Note variant="error">{t("modal:confirmSignpostSchoolDeletionModal.alert")}</Note>
          <CssFlex justifyContent="space-between" marginTop={2} gap={1}>
            <CssButton disabled={!!isDeleting} onClick={closeModal}>
              {t("modal:confirmSignpostSchoolDeletionModal.actionButtons.cancel")}
            </CssButton>
            <CssButton
              data-cy="confirm-school-delete-button"
              variant="error"
              isLoading={!!isDeleting}
              onClick={onConfirmDeleteClick}
            >
              {t("modal:confirmSignpostSchoolDeletionModal.actionButtons.confirm")}
            </CssButton>
          </CssFlex>
        </CssFlex>
      </Modal>
    );
  });
